import { useQuery } from '@tanstack/react-query';

import apiClient from '../axios';
import {
  DEVICE_AVAILABILITY_EXPECTED_HTTP_STATUSES,
  DEVICE_AVAILABILITY_ROUTE,
} from '../constants/device-availability.js';
import { getApiErrorStatus } from '../lib/error.js';

export const isExpectedAvailabilityRejection = (error: unknown): boolean => {
  const status = getApiErrorStatus(error);
  return (
    status !== undefined &&
    (DEVICE_AVAILABILITY_EXPECTED_HTTP_STATUSES as readonly number[]).includes(status)
  );
};

export type AvailabilityState = 'online' | 'offline' | 'nodata';

export interface DeviceAvailabilityParams {
  deviceIds: string[];
  from: string;
  to?: string;
}

export interface DeviceAvailability {
  deviceId: string;
  availabilityPct: number | null;
  onlineMs: number;
  offlineMs: number;
  nodataMs: number;
  disconnects: number;
  longestOfflineMs: number;
  timeline: AvailabilityState[];
}

export interface DeviceAvailabilityResponse {
  from: string;
  to: string;
  trackingStartedAt: string | null;
  bucketMs: number;
  devices: DeviceAvailability[];
}

export const useDeviceAvailability = (
  { deviceIds, from, to }: DeviceAvailabilityParams,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const sortedDeviceIds = [...deviceIds].sort();
  return useQuery({
    queryKey: ['device-availability', { deviceIds: sortedDeviceIds, from, to }],
    queryFn: async () => {
      const { data } = await apiClient.get<DeviceAvailabilityResponse>(DEVICE_AVAILABILITY_ROUTE, {
        params: {
          deviceIds: sortedDeviceIds.join(','),
          from: new Date(from).toISOString(),
          ...(to ? { to: new Date(to).toISOString() } : {}),
        },
      });
      return data;
    },
    enabled,
    // An expected rejection answers the same way again, and a repeated 422 re-runs the costly scan.
    retry: (failureCount, error) => failureCount < 1 && !isExpectedAvailabilityRejection(error),
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
};
